import { useState, useEffect } from 'react';

function getStorageValue(key, defaultValue, migrate) {
  // getting stored value
  const saved = localStorage.getItem(key);
  let initial = JSON.parse(saved);
  if (initial != null && migrate) {
    initial = migrate(initial);
  }
  return initial ?? defaultValue;
}

export const useLocalStorage = (key, defaultValue, migrate) => {
  const [value, setValue] = useState(() => {
    return getStorageValue(key, defaultValue, migrate);
  });

  useEffect(() => {
    // storing input name
    localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);

  return [value, setValue];
};

function patchStorageSetItem(storage, storageName, allowClearAll) {
  const originalSetItem = storage.setItem.bind(storage);

  storage.setItem = (key, value) => {
    const MAX_ATTEMPTS = 5;
    let attempt = 0;
    let lastError = null;

    while (attempt < MAX_ATTEMPTS) {
      try {
        originalSetItem(key, value);
        return;
      } catch (error) {
        lastError = error;
        attempt++;

        if (attempt >= MAX_ATTEMPTS) {
          console.error(
            `Failed to set ${storageName} after ${MAX_ATTEMPTS} attempts for key: ${key}`,
            error
          );
          break;
        }

        try {
          const allKeys = Object.keys(storage);

          if (allKeys.length === 0) {
            console.error(
              `Cannot set ${storageName} item - storage is empty but item is too large: ${key}`
            );
            break;
          }

          // On last attempt, clear everything (localStorage only)
          if (allowClearAll && attempt >= MAX_ATTEMPTS - 1) {
            console.warn(`Clearing all ${storageName} as last resort`);
            for (const k of allKeys) {
              storage.removeItem(k);
            }
          } else {
            const items = allKeys.map((k) => {
              const raw = storage.getItem(k);
              let lastAccessDate = null;
              try {
                const data = JSON.parse(raw);
                lastAccessDate = data?.lastAccessDate ?? null;
              } catch (e) {
                // Non-JSON value, no timestamp available
              }
              return { key: k, lastAccessDate };
            });

            // Only evict items that have a lastAccessDate.
            // Sort by lastAccessDate (oldest first).
            const evictable = items
              .filter((i) => i.lastAccessDate != null)
              .sort((a, b) => a.lastAccessDate - b.lastAccessDate);

            if (evictable.length === 0) {
              break;
            }
            storage.removeItem(evictable[0].key);
          }
        } catch (cleanupError) {
          console.error(`Error during ${storageName} cleanup:`, cleanupError);
          break;
        }
      }
    }

    if (lastError) {
      console.error(`Final error setting ${storageName}:`, lastError);
    }
  };
}

export const overloadStorageForRetries = () => {
  if (!window._storagePatched) {
    patchStorageSetItem(window.localStorage, 'localStorage', true);
    patchStorageSetItem(window.sessionStorage, 'sessionStorage', false);
    window._storagePatched = true;
  }
};
